<script setup lang="ts">
import { ref, watch } from 'vue'
import { useFileDialog } from '@vueuse/core'
import VIcon from './v-icon.vue'

const props = withDefaults(
  defineProps<{
    image: string | null
    accept?: string
    label?: string
  }>(),
  {
    image: null,
    accept: '.png,.jpg,.jpeg',
    label: 'Photo',
  },
)

const emit = defineEmits<{
  upload: [file: File]
  remove: []
}>()

const preview = ref<string | null>(props.image)

watch(
  () => props.image,
  (value) => {
    preview.value = value
  },
)

const { files, open, reset } = useFileDialog({
  accept: props.accept,
  multiple: false,
})

const openDialog = () => open()

watch(files, (fileList) => {
  if (fileList && fileList.length > 0) {
    const image = fileList[0]
    const reader = new FileReader()
    reader.onload = (e) => {
      preview.value = e.target?.result as string
    }
    reader.readAsDataURL(image)
    emit('upload', image)
    reset()
  }
})

const remove = () => {
  preview.value = null
  emit('remove')
}
</script>

<template>
  <div class="group relative cursor-pointer" @click="openDialog">
    <div
      class="flex h-24 w-24 items-center justify-center overflow-hidden rounded-xl bg-brand/[0.06] ring-1 ring-inset ring-brand/30 transition-all group-hover:ring-brand/50"
    >
      <img v-if="preview" :src="preview" :alt="label" class="h-full w-full object-contain" @error="preview = null" />
      <div v-else class="flex flex-col items-center gap-0.5 text-brand">
        <VIcon name="Upload" class="h-5 w-5" />
        <span v-if="label" class="text-[10px] font-medium">{{ label }}</span>
      </div>
    </div>
    <button
      v-if="!preview"
      type="button"
      @click.stop="openDialog"
      class="absolute -bottom-1.5 -right-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-gray-200 transition-transform hover:scale-110"
    >
      <VIcon name="Plus" class="h-3.5 w-3.5 text-gray-600" />
    </button>
    <template v-if="preview">
      <button
        type="button"
        @click.stop="openDialog"
        class="absolute -bottom-1.5 -left-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-gray-200 transition-transform hover:scale-110"
      >
        <VIcon name="Pencil" class="h-3.5 w-3.5 !fill-gray-800" />
      </button>
      <button
        type="button"
        @click.stop="remove"
        class="absolute -bottom-1.5 -right-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-gray-200 transition-transform hover:scale-110"
      >
        <VIcon name="Trash" class="h-3.5 w-3.5 text-red-500" />
      </button>
    </template>
  </div>
</template>
